import { useEffect, useRef, useState } from 'react';
import NarrowDrawer from "../../../../../../../components/general/NarrowDrawer.tsx";
import { PrimaryButton } from "../../../../../../../components/general/PrimaryButton.tsx";
import { SecondaryButton } from "../../../../../../../components/general/SecondaryButton.tsx";
import MultiSelectDropdown from "../../../../../../../components/form/MultiSelectDropdown";
import {
    MagnifyingGlassIcon,
    BuildingOffice2Icon,
    ArrowLeftIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import Dropdown from "../../../../../../../components/general/Dropdown.tsx";
import { BookkeepingConnection } from "./useBKSDocumentImport.ts";
import {
    BKSConnectionMapping,
    BKSEntrepreneurBusiness,
    BKSImportParams,
    BKSJournal,
} from "./bksDocumentImport.types";

interface ImportBookkeepingDocumentsDrawerProps {
    open: boolean;
    onClose: () => void;
    connections: BookkeepingConnection[];
    selectedConnectionId: string | null;
    onConnectionChange: (id: string) => void;
    bookkeepingAdministrations: BKSEntrepreneurBusiness[];
    journals: BKSJournal[];
    connectionMapping?: BKSConnectionMapping;
    hasActiveJobForSelectedConnection: boolean;
    onImportStart: (params: Omit<BKSImportParams, 'connectionId'> & { connectionId?: string }) => Promise<void>;
    onAdministrationSelect?: (administrationId: string) => void;
}

export function ImportBookkeepingDocumentsDrawer({
    open,
    onClose,
    connections,
    selectedConnectionId,
    onConnectionChange,
    bookkeepingAdministrations,
    journals,
    connectionMapping,
    hasActiveJobForSelectedConnection,
    onImportStart,
    onAdministrationSelect,
}: ImportBookkeepingDocumentsDrawerProps) {
    const [selectedAdministration, setSelectedAdministration] = useState<BKSEntrepreneurBusiness | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedJournals, setSelectedJournals] = useState<string[]>([]);
    const [pendingMappedJournalIds, setPendingMappedJournalIds] = useState<string[] | null>(null);
    const mappingPrefillKeyRef = useRef<string | null>(null);
    const [isStartingImport, setIsStartingImport] = useState(false);

    useEffect(() => {
        if (!open) {
            setSelectedAdministration(null);
            setSearchQuery('');
            setStartDate('');
            setEndDate('');
            setSelectedJournals([]);
            setPendingMappedJournalIds(null);
            mappingPrefillKeyRef.current = null;
        }
    }, [open]);

    useEffect(() => {
        if (!open || !connectionMapping || !selectedConnectionId || bookkeepingAdministrations.length === 0) {
            return;
        }

        const prefillKey = `${selectedConnectionId}:${connectionMapping.external_business_id}`;
        if (mappingPrefillKeyRef.current === prefillKey) {
            return;
        }
        mappingPrefillKeyRef.current = prefillKey;

        const mappedAdministration = bookkeepingAdministrations.find(
            (administration) => administration.id === connectionMapping.external_business_id,
        );

        if (mappedAdministration) {
            setSelectedAdministration(mappedAdministration);
            setSelectedJournals([]);
            setPendingMappedJournalIds(connectionMapping.journal_ids || []);
            onAdministrationSelect?.(mappedAdministration.id);
        } else {
            setPendingMappedJournalIds(null);
        }
    }, [open, connectionMapping, selectedConnectionId, bookkeepingAdministrations, onAdministrationSelect]);

    useEffect(() => {
        if (
            pendingMappedJournalIds === null
            || !selectedAdministration
            || !connectionMapping
            || selectedAdministration.id !== connectionMapping.external_business_id
            || journals.length === 0
        ) {
            return;
        }

        const availableJournalIds = new Set(journals.map((journal) => journal.id));
        const validJournalIds = pendingMappedJournalIds.filter((journalId) => availableJournalIds.has(journalId));
        setSelectedJournals(validJournalIds);
        setPendingMappedJournalIds(null);
    }, [pendingMappedJournalIds, selectedAdministration, connectionMapping, journals]);

    const handleStartImport = async () => {
        if (!selectedAdministration || !startDate || !endDate || selectedJournals.length === 0 || !selectedConnectionId) {
            return;
        }

        setIsStartingImport(true);
        try {
            await onImportStart({
                administrationId: selectedAdministration.id,
                administrationName: selectedAdministration.name,
                startDate,
                endDate,
                journals: selectedJournals,
            });

            setSelectedAdministration(null);
            setStartDate('');
            setEndDate('');
            setSelectedJournals([]);
            setPendingMappedJournalIds(null);
            mappingPrefillKeyRef.current = null;
            onClose();
        } catch {
            // Error toast is shown by the parent handler.
        } finally {
            setIsStartingImport(false);
        }
    };

    const handleSelectAdministration = (administration: BKSEntrepreneurBusiness) => {
        mappingPrefillKeyRef.current = null;
        setPendingMappedJournalIds(null);
        setSelectedAdministration(administration);
        setSelectedJournals([]);
        onAdministrationSelect?.(administration.id);
    };

    const handleBack = () => {
        mappingPrefillKeyRef.current = null;
        setPendingMappedJournalIds(null);
        setSelectedAdministration(null);
        setStartDate('');
        setEndDate('');
        setSelectedJournals([]);
    };

    const filteredAdministrations = bookkeepingAdministrations.filter((administration) =>
        administration.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    const isMappingValid = Boolean(selectedAdministration && selectedJournals.length > 0 && selectedConnectionId);
    const isFormValid = startDate && endDate && isMappingValid && !hasActiveJobForSelectedConnection && !isStartingImport;

    const renderAdministrationList = () => (
        <div className="mt-4 flex flex-col gap-5">
            <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                    type="text"
                    placeholder="Zoek administratie..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full rounded-lg border-0 py-2 pl-9 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
            </div>

            <p className="text-sm text-gray-500">
                Selecteer de administratie waarvan je documenten wilt importeren.
            </p>

            {filteredAdministrations.length === 0 && (
                <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
                    <BuildingOffice2Icon className="mx-auto h-8 w-8 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500">Geen administraties gevonden.</p>
                </div>
            )}

            {filteredAdministrations.length > 0 && (
                <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 overflow-hidden">
                    {filteredAdministrations.map((administration) => (
                        <li
                            key={administration.id}
                            className="flex justify-between items-center px-4 py-3.5 hover:bg-gray-50 transition-colors"
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 bg-[var(--superliteprimary)]">
                                    <BuildingOffice2Icon className="h-4 w-4 text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <span className="text-sm font-medium text-gray-900 block truncate">{administration.name}</span>
                                    {administration.vat && (
                                        <span className="text-xs text-gray-400">{administration.vat}</span>
                                    )}
                                </div>
                            </div>
                            <SecondaryButton
                                title="Selecteer"
                                onClick={() => handleSelectAdministration(administration)}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );

    const renderConfigForm = () => (
        <div className="mt-4 flex flex-col gap-6">
            <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors self-start"
            >
                <ArrowLeftIcon className="h-4 w-4" />
                Kies een andere administratie
            </button>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 bg-[var(--superliteprimary)]">
                    <BuildingOffice2Icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                    <span className="text-sm font-medium text-gray-900">{selectedAdministration?.name}</span>
                    {selectedAdministration?.vat && (
                        <span className="text-xs text-gray-400 block">{selectedAdministration.vat}</span>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <label className="block text-sm font-medium text-gray-700">Periode</label>
                <div className="flex gap-3 items-center">
                    <div className="flex-1">
                        <label className="block text-xs text-gray-400 mb-1">Startdatum</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                        />
                    </div>
                    <span className="text-gray-300 mt-5">—</span>
                    <div className="flex-1">
                        <label className="block text-xs text-gray-400 mb-1">Einddatum</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <label className="block text-sm font-medium text-gray-700">Dagboeken</label>
                <MultiSelectDropdown
                    options={journals}
                    selectedValues={selectedJournals}
                    onSelectionChange={setSelectedJournals}
                    placeholder="Selecteer dagboeken..."
                    displayValue={(id) => journals.find((journal) => journal.id === id)?.name || id}
                    className="w-full"
                />
            </div>

            {hasActiveJobForSelectedConnection && (
                <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <span>Er loopt al een import voor deze koppeling. Wacht tot deze is afgerond voordat je een nieuwe start.</span>
                </div>
            )}

            <div className="pt-2">
                <PrimaryButton
                    title={isStartingImport ? 'Import starten...' : 'Start import'}
                    onClick={() => void handleStartImport()}
                    disabled={!isFormValid}
                    disabledFeedback={hasActiveJobForSelectedConnection
                        ? 'Er loopt al een import voor deze koppeling'
                        : 'Selecteer een periode en minstens één dagboek'}
                />
            </div>
        </div>
    );

    return (
        <NarrowDrawer
            open={open}
            updateOpenState={(isOpen) => { if (!isOpen) onClose(); }}
            title="Importeer documenten uit boekhoudpakket"
        >
            {connections.length > 0 && (
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Boekhoudkoppeling</label>
                    {connections.length === 1 ? (
                        <p className="text-sm text-gray-600">{connections[0].name}</p>
                    ) : (
                        <Dropdown
                            selected={{
                                value: selectedConnectionId || '',
                                label: connections.find((connection) => connection.id === selectedConnectionId)?.name || 'Selecteer tool',
                            }}
                            options={connections.map((connection) => ({ value: connection.id, label: connection.name }))}
                            onUpdateFunc={(value) => {
                                onConnectionChange(value.toString());
                                mappingPrefillKeyRef.current = null;
                                setPendingMappedJournalIds(null);
                                setSelectedAdministration(null);
                                setStartDate('');
                                setEndDate('');
                                setSelectedJournals([]);
                            }}
                        />
                    )}
                </div>
            )}
            {selectedAdministration ? renderConfigForm() : renderAdministrationList()}
        </NarrowDrawer>
    );
}
